import { CartStore } from "assets/cart";
import { ProductCardsBlock } from "shopify-accelerate/@types/blocks";
import { CardProductCardCtaButtonBlock, MainProductGalleryBlock, MainProductLabelsBlock, MainProductThumbnailsBlock } from "types/blocks";
import { MainProductSection } from "types/sections";
import { _Product_hydrated, _Variant_hydrated } from "../../assets/types";

const initMainProduct = ($formEl: HTMLFormElement, $refs: object, productHandle: string, isProductBar = false) => {
  const gallery = $formEl.querySelector<HTMLElement>(`[x-ref="gallery"]`);
  const thumbnails = $formEl.querySelector<HTMLElement>(`[x-ref="thumbnails"]`);
  const scrollContainer = gallery?.firstElementChild;
  let init = true;
  const preorder_threshold = +(window.theme_settings.data__product__variant__preorder_threshold ?? 1);

  const cart = window.Alpine.store("cart") as CartStore;
  const isPrimary =
    !isProductBar &&
    new RegExp(`^/(products|collections/[^/]*/products)/${encodeURIComponent(productHandle)}`, "i").test(
      _stores?.router?.pathname ?? ""
    );

  const quickView = $formEl.hasAttribute("data-quick-view");
  const settings = utils.JSONParse<MainProductSection["settings"]>($formEl.getAttribute("data-settings"));

  const product = utils.JSONParse<
    _Product_hydrated & {
      selected_or_first_available_variant_id?: number;
      selected_variant_id?: number;
    }
  >(
    document.querySelector(`[data-primary-product-data="${productHandle}"]`)?.innerHTML ??
      document.querySelector(`[data-product-data="${productHandle}"]`)?.innerHTML
  );

  if (isPrimary) {
    const url = new URL(window.location.href);
    const variantId = url.searchParams.get("variant");
    product.selected_variant_id = variantId ? +variantId : product.selected_variant_id;
  }

  const selected_variant = _product.getSelectedVariant(product, product.selected_variant_id);

  const selling_plan_allocations = selected_variant?.selling_plan_allocations;

  const selected_selling_plan = selling_plan_allocations?.[0]?.selling_plan;

  const selling_plan_discount_wording = +selected_selling_plan?.price_adjustments?.[0]?.value
    ? selected_selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
      ? `${utils.formatMoney(selected_selling_plan?.price_adjustments?.[0]?.value)}`
      : selected_selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
      ? `${selected_selling_plan?.price_adjustments?.[0]?.value}%`
      : ""
    : "";

  const gallerySettings = utils.JSONParse<MainProductGalleryBlock["settings"]>(gallery?.getAttribute("data-settings"));

  const thumbnailSettings = utils.JSONParse<MainProductThumbnailsBlock["settings"]>(thumbnails?.getAttribute("data-settings"));

  const mediaElements = [...(gallery?.querySelectorAll("[data-main-product-images] [data-media-id]") ?? [])].map((element) => ({
    element,
    mediaId: +element.getAttribute("data-media-id"),
    metafield: element.hasAttribute("data-metafield-media"),
    media:
      product?.media?.find((media) => media.id === +element.getAttribute("data-media-id")) ??
      product?.variants
        ?.find((v) => v.metafields?.smart?.images?.some((image) => image.id === +element.getAttribute("data-media-id")))
        ?.metafields?.smart?.images?.find((image) => image.id === +element.getAttribute("data-media-id")),
  }));

  const thumbnailMediaElements = [...(thumbnails?.querySelectorAll("[data-media-id]") ?? [])].map((element) => ({
    element,
    mediaId: +element.getAttribute("data-media-id"),
    metafield: element.hasAttribute("data-metafield-media"),
    media:
      product?.media?.find((media) => media.id === +element.getAttribute("data-media-id")) ??
      product?.variants
        ?.find((v) => v.metafields?.smart?.images?.some((image) => image.id === +element.getAttribute("data-media-id")))
        ?.metafields?.smart?.images?.find((image) => image.id === +element.getAttribute("data-media-id")),
  }));

  let discounted_price = selected_variant?.price;
  const price_adjustment =
    selected_selling_plan?.price_adjustments?.[0] ??
    selected_variant?.selling_plan_allocations?.[0]?.selling_plan?.price_adjustments?.[0];

  if (price_adjustment?.value_type === "fixed_amount") {
    discounted_price = Math.max(discounted_price - Math.round(+price_adjustment?.value), 0);
  }

  if (price_adjustment?.value_type === "percentage") {
    discounted_price = Math.max(discounted_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
  }

  const available_quantity =
    selected_variant?.inventory_management === "shopify" && selected_variant?.inventory_policy === "deny"
      ? selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
      : selected_variant?.preorder && selected_variant?.preorder_quantity
      ? selected_variant?.inventory_quantity +
        selected_variant?.preorder_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
      : 99999;

  const state = Alpine.reactive({
    product_handle: productHandle,
    element: $formEl,
    count: 0,
    manual_discount: 0,
    isPrimary: isPrimary,
    product: product as _Product_hydrated,
    selected_media_id: product?.media?.[0]?.id,
    previous_selected_media_id: product?.media?.[0]?.id,
    show_complementary_products: true,
    properties: {},
    selected_variant,
    selling_plan_allocations,
    selected_price: discounted_price,
    selected_compare_at_price: Math.max(selected_variant?.compare_at_price, selected_variant?.price),
    upsell_total: 0,
    upsell_compare_at_total: 0,
    selected_selling_plan: null as typeof selected_selling_plan,
    last_selected_selling_plan: selected_selling_plan,
    selling_plan_discount_wording,
    isAdding: false,
    dynamic_buy_button: null as HTMLButtonElement,
    options: selected_variant?.options,
    quantity: 1,
    quantity_limit: available_quantity,
    sibling_handle: "",
    disable_add_to_cart: new Set<string>(),
    hasVariants: product?.variants?.length > 1 || product?.variants?.[0]?.title !== "Default Title",
    hasSubscription: !!selected_variant?.options?.length,
    soldOut: available_quantity <= 0,
    available_quantity: available_quantity,
    preorder:
      selected_variant?.preorder &&
      selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0) <
        preorder_threshold,
    upsell_items: new Map<
      /* Product Handle */
      string,
      {
        id: number;
        quantity?: number;
        properties?: {
          [T: string]: string;
        };
        bundle?: boolean;
        auto_addon?: boolean;
        variable_quantity?: boolean;
        selling_plan?: number;
        variant?: _Variant_hydrated;
      }
    >(),
    scrolling_to_image: false,
  });

  const handlePopState = (e) => {
    const url = new URL(window.location.href);
    const selling_plan_id = +url.searchParams.get("selling_plan");
    const variant_id = +url.searchParams.get("variant");

    if (!variant_id) return;

    if (state.selected_variant?.id !== variant_id) {
      state.selected_variant = state.product?.variants?.find((variant) => variant.id === variant_id) ?? state.selected_variant;
      state.options = state.selected_variant?.options;
    }

    if (selling_plan_id !== state.selected_selling_plan?.id) {
      state.selected_selling_plan =
        state.selected_variant?.selling_plan_allocations?.find((plan) => plan.selling_plan.id === selling_plan_id)
          ?.selling_plan ?? state.selected_selling_plan;
    }
  };

  const styleDynamicBuyButton = (element: HTMLElement) => {
    const cssClass = element.getAttribute("data-button-class");
    const previewButton = element.querySelector("[data-pre-styled-button]");

    element.style.setProperty("--shopify-accelerated-checkout-button-block-size", getComputedStyle(previewButton)?.height);
    element.style.setProperty(
      "--shopify-accelerated-checkout-button-border-radius",
      getComputedStyle(previewButton)?.borderRadius
    );
    previewButton.remove();
  };

  const handleAddToCart = async (e: PointerEvent) => {
    if (state.disable_add_to_cart.size > 0) return;

    const random_id = utils.shortUUID();

    const properties = {};

    if (state.upsell_items.size && [...state.upsell_items.values()]?.some((item) => item.bundle)) {
      properties["_p_id"] = `${random_id}`;
    }

    if (state.selected_variant?.preorder && state.selected_variant?.inventory_quantity < preorder_threshold) {
      properties["Preorder"] = `true`;
      if (state.selected_variant?.preorder_date) {
        properties["Preorder"] = `Shipping ${new Date(state.selected_variant.preorder_date).toLocaleDateString(
          navigator.language,
          {
            month: "short",
            year: "numeric",
          }
        )}`;
      }
    }

    const formElement =
      $formEl.tagName === "FORM"
        ? $formEl
        : document.querySelector<HTMLFormElement>(`form[data-handle="${state.product?.handle ?? productHandle}"]`);

    if (formElement.tagName === "FORM") {
      Object.entries(utils.serializeForm(formElement))?.forEach(([key, value]) => {
        if (key.includes("properties[") && value) {
          properties[key.replace(/^properties\[(.*)]$/gi, "$1")] = value;
        }
      });
    }

    if (properties?.["__shopify_send_gift_card_to_recipient"] && !utils.isEmail(properties?.["Recipient email"])) {
      utils.validateFormAndToast(formElement);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    state.isAdding = true;
    const data = await _cart.add({
      items: [
        {
          id: state.selected_variant.id,
          quantity: state.quantity,
          selling_plan: cart.global_subscriptions
            ? state.selected_variant?.selling_plan_allocations?.find(
                (plan) => plan.selling_plan?.id === cart.cart_selling_plan_id
              )?.selling_plan?.id
            : state.selected_selling_plan?.id,
          properties: {
            ...properties,
            ...state.properties,
          },
        },
        ...[...state.upsell_items.values()].map((entry) => ({
          id: entry.id,
          selling_plan: cart.global_subscriptions
            ? state.selected_variant?.selling_plan_allocations?.find(
                (plan) => plan.selling_plan?.id === cart.cart_selling_plan_id
              )?.selling_plan?.id
            : entry.variant?.selling_plan_allocations?.find((plan) => plan.selling_plan?.id === state.selected_selling_plan?.id)
                ?.selling_plan?.id,
          ...(entry?.bundle
            ? {
                parent_id: state.selected_variant.id,
              }
            : {}),
          quantity: state.quantity * entry.quantity,
          properties: {
            ...entry.properties,
            _p_id_link: entry?.bundle ? `${random_id}` : undefined,
            _p_required: entry?.auto_addon ? "true" : undefined,
            _p_fixed_quantity: !entry?.variable_quantity ? "true" : undefined,
            Preorder:
              entry?.variant?.preorder && entry?.variant?.inventory_quantity < preorder_threshold
                ? entry?.variant?.preorder_date
                  ? `Shipping ${new Date(entry?.variant.preorder_date).toLocaleDateString(navigator.language, {
                      month: "short",
                      year: "numeric",
                    })}`
                  : "true"
                : undefined,
          },
        })),
      ],
    });

    state.isAdding = false;
    if (!data.cart_error) {
      _stores.modal.setId("modal--cart-drawer");
      _stores.quickView.show = false;
    }
  };

  const handleBackInStockNotification = async (e: PointerEvent) => {
    if (state?.selected_variant?.available && !state?.selected_variant?.preorder) return;
    e.preventDefault();
    e.stopPropagation();
    _stores.modal.setId("back_in_stock");
    _stores.backInStockNotification.product = state.product;
    _stores.backInStockNotification.selected_variant = state.selected_variant;
  };

  const setSelectedVariant = (id: number, scroll_to_variant = true) => {
    state.selected_variant = state.product.variants?.find((variant) => variant.id === id);
    state.options = state.selected_variant?.options;

    if (
      state.selected_selling_plan &&
      !state.selected_variant?.selling_plan_allocations?.some(
        (plan) => plan?.selling_plan?.id === state.selected_selling_plan?.id
      )
    ) {
      state.selected_selling_plan = state.selected_variant?.selling_plan_allocations?.[0]?.selling_plan ?? null;
    }

    if (state.product.requires_selling_plan && !state.selected_selling_plan) {
      state.selected_selling_plan =
        state.selected_variant?.selling_plan_allocations?.[0]?.selling_plan ??
        state.product.selling_plan_groups?.[0]?.selling_plans?.[0];
    }

    if (gallerySettings?.scroll_to_selected_variant_image && state.selected_variant?.featured_media?.id && scroll_to_variant) {
      scrollToMediaById(state.selected_variant?.featured_media?.id, false);
    }
    _product.lastOptions = {
      ...(_product.lastOptions ?? {}),
      ...(state.product.options_with_values?.reduce((acc, option, index) => {
        acc[utils.handlelize(option?.name)] = state.options[index];
        return acc;
      }, {}) ?? {}),
    };
    setTimeout(() => {
      sessionStorage.setItem("_p_last_options", JSON.stringify(_product.lastOptions));
    });
  };

  const setProductOption = ({ index, value }: { index: number; value: string }) => {
    const options = [...state.options];
    options[index] = value;

    setSelectedVariant(
      state.product?.variants?.find((variant) => variant.options.every((option, i) => options[i] === option))?.id ??
        state.product?.variants?.find((variant) => variant.options[index] === value)?.id ??
        state.product?.variants?.find(({ available }) => available)?.id ??
        state.product?.variants?.[0]?.id
    );
  };

  const setSellingPlan = (selling_plan_id: number | undefined) => {
    state.selected_selling_plan = state.selling_plan_allocations.find(
      (allocation) => allocation.selling_plan.id === selling_plan_id
    )?.selling_plan;

    if (state.selected_selling_plan) {
      state.last_selected_selling_plan = state.selected_selling_plan;
    }

    const selling_plan = state.selected_selling_plan ?? selling_plan_allocations?.[0]?.selling_plan;

    state.selling_plan_discount_wording = selling_plan?.price_adjustments?.[0]?.value
      ? selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
        ? `${utils.formatMoney(selling_plan?.price_adjustments?.[0]?.value)}`
        : selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
        ? `${selling_plan?.price_adjustments?.[0]?.value}%`
        : ""
      : "";
  };

  const setSiblingProduct = (handle: string, quickView) => {
    if (handle === state.product.handle) return;
    _product.lastOptions = {
      ...(_product.lastOptions ?? {}),
      ...(state.product.options_with_values?.reduce((acc, option, index) => {
        acc[utils.handlelize(option?.name)] = state.options[index];
        return acc;
      }, {}) ?? {}),
    };
    setTimeout(() => {
      sessionStorage.setItem("_p_last_options", JSON.stringify(_product.lastOptions));
    });
    if (quickView) {
      _stores.quickView.renderQuickView(handle);
      return;
    }

    barba.go(utils.getSiblingUrl(handle));
  };

  const setQuantity = (quantity: number) => {
    state.quantity = quantity;
  };

  const handleStickyScroll = () => {};

  const scrollToMediaById = async (mediaId: number, no_variant_select = true) => {
    const isHorizontal = () => {
      return !!(
        getComputedStyle(scrollContainer).overflowY === "hidden" ||
        getComputedStyle(scrollContainer).gridAutoFlow === "column" ||
        scrollContainer.scrollWidth > scrollContainer?.clientWidth
      );
    };

    const image = gallery?.querySelector<HTMLElement>(`[data-media-id="${mediaId}"]`);

    if (thumbnailSettings?.select_variant_on_click && no_variant_select) {
      setSelectedVariant(
        state.product.variants.find((v) => v.featured_media?.id === mediaId)?.id || state.selected_variant?.id,
        false
      );
    }
    if (!image) return;

    if (state.selected_media_id !== mediaId) {
      state.previous_selected_media_id = state.selected_media_id;
    }
    state.selected_media_id = mediaId;
    await utils.delay(1);
    state.scrolling_to_image = true;

    const header = document.querySelector<HTMLElement>(".header-sections-container");
    const offsetHeight = Math.max(180, header?.getBoundingClientRect()?.bottom ?? 0);
    const imageOffset = utils.getElementPosition(image);

    if (utils.isElementScrollable(scrollContainer)) {
      let scrollSnap = false;
      if (["snap-both", "snap-x", "snap-y"].some((c) => scrollContainer?.classList?.contains(c))) {
        scrollContainer?.classList.remove("snap-both", "snap-x", "snap-y");
        scrollSnap = true;
      }
      utils.scrollToXY(200, image.offsetLeft, image.offsetTop, scrollContainer as HTMLElement, () => {
        if (scrollSnap) {
          scrollContainer?.classList.add(isHorizontal() ? "snap-x" : "snap-y");
        }
        if (state.selected_media_id !== mediaId) {
          state.previous_selected_media_id = state.selected_media_id;
        }
        state.selected_media_id = mediaId;
        state.scrolling_to_image = false;
      });
    } else {
      utils.scrollToXY(
        200,
        image.offsetLeft,
        imageOffset.top - offsetHeight - +getComputedStyle(gallery).paddingTop.replace("px", ""),
        window,
        () => {
          if (state.selected_media_id !== mediaId) {
            state.previous_selected_media_id = state.selected_media_id;
          }
          state.selected_media_id = mediaId;
          state.scrolling_to_image = false;
        }
      );
    }
  };

  const renderProductCards = async (
    element: HTMLElement,
    primary_source: ProductCardsBlock["settings"]["primary_source"],
    secondary_source: ProductCardsBlock["settings"]["fallback_source"],
    product_class: string,
    desktop_display_limit: number,
    mobile_display_limit: number
  ) => {
    const limit = Math.max(desktop_display_limit, mobile_display_limit);
    const fallback_products = utils.JSONParse<_Product_hydrated[]>(element.getAttribute("data-fallback-products"));

    const products = [];

    switch (primary_source) {
      case "complementary": {
        state.product?.complementary_products?.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "related": {
        state.product?.related_products?.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "recently_viewed": {
        const recent_products = await Promise.all(
          (window?._recent_products ?? [])?.map(([handle, product_id]) => _product.getHydratedProductData(handle, product_id))
        );
        recent_products.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "manual": {
        fallback_products.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
    }

    switch (secondary_source) {
      case "complementary": {
        state.product?.complementary_products?.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "related": {
        state.product?.related_products?.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "recently_viewed": {
        const recent_products = await Promise.all(
          (window?._recent_products ?? []).map(([handle, product_id]) => _product.getHydratedProductData(handle, product_id))
        );

        recent_products.forEach((prod) => {
          products.push(prod);
        });
        break;
      }
      case "manual": {
        break;
      }
    }

    const renderProducts =
      products
        ?.filter((prod) => prod && prod.id !== state.product.id)
        ?.filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i)
        ?.filter((prod, index) => prod.handle !== element.children?.[index]?.getAttribute("data-product-handle"))
        ?.slice(0, limit) ?? [];

    if (!renderProducts.length) {
      return;
    }

    const nonProductElements = element.querySelectorAll(":scope > :not([data-product-handle])");
    element.innerHTML = "";
    nonProductElements.forEach((el) => {
      element.appendChild(el);
    });

    renderProducts.forEach((prod, i, arr) => {
      const node = document.querySelector(`[data-product-card='${product_class}']`)?.cloneNode(true) as HTMLElement;

      if (node) {
        const div = document.createElement("div");
        div.setAttribute("data-product-handle", prod.handle);
        div.setAttribute(
          "class",
          `shrink-0 max-w-full w-full h-full order-[--order] ${
            i > desktop_display_limit ? "mobile-only" : i > mobile_display_limit ? "desktop-tablet-only" : ""
          }`
        );
        div.style.setProperty("--order", `${(i + 1) * 10}`);
        node.removeAttribute(`data-product-card`);
        node.setAttribute("data-product-handle", prod.handle);
        node.setAttribute("data-product-id", prod.id);
        node.classList.add("card-loading");
        node.querySelectorAll("[data-loop-item], [data-x-if], style").forEach((el) => {
          el.remove();
        });
        div.appendChild(node);
        element.appendChild(div);
      }
    });
  };

  const handleImageSelection = (
    filter_images:
      | MainProductGalleryBlock["settings"]["filter_images"]
      | MainProductThumbnailsBlock["settings"]["thumbnail_filter_images"],
    items: typeof mediaElements,
    variant: typeof selected_variant
  ) => {
    const hydrateImages = (show: boolean, element: Element) => {
      if (show) {
        element.querySelectorAll<HTMLImageElement | HTMLSourceElement>("img[data-srcset], img[data-src]").forEach((img) => {
          const src = img.getAttribute("data-src");
          const srcset = img.getAttribute("data-srcset");
          if (src) {
            img.setAttribute("src", src);
            img.removeAttribute("data-src");
          }
          if (srcset) {
            img.setAttribute("srcset", srcset);
            img.removeAttribute("data-srcset");
          }
        });
      }
    };
    switch (filter_images) {
      case "show_all_variants": {
        items?.forEach(({ element, mediaId }) => {
          element.classList.toggle("hidden", !state.product.variants.some((v) => v?.featured_media?.id === mediaId));
          hydrateImages(
            state.product.variants.some((v) => v?.featured_media?.id === mediaId),
            element
          );
        });
        return;
      }
      case "show_all": {
        items?.forEach(({ element, mediaId }) => {
          element.classList.remove("hidden");
          hydrateImages(true, element);
        });
        return;
      }
      case "selected_variant": {
        items?.forEach(({ element, mediaId }) => {
          element.classList.toggle("hidden", variant?.featured_media?.id !== mediaId);
          hydrateImages(variant?.featured_media?.id === mediaId, element);
        });
        return;
      }
      case "variant_images_by_order": {
        let hide = true;

        items?.forEach(({ element, mediaId }) => {
          if (variant.featured_media?.id === mediaId) {
            hide = false;
            element.classList.toggle("hidden", hide);
            hydrateImages(!hide, element);
            return;
          }
          if (state.product.variants.map((v) => v?.featured_media?.id).includes(mediaId)) {
            hide = true;
          }
          element.classList.toggle("hidden", hide);
          hydrateImages(!hide, element);
        });
        return;
      }
      case "variant_images_by_metafield": {
        if (variant?.featured_media === null && !state.selected_variant?.metafields?.smart?.images) {
          items?.forEach(({ element, mediaId }) => {
            element.classList.remove("hidden");
            hydrateImages(true, element);
          });
          return;
        } else {
          items?.forEach(({ element, mediaId, metafield }) => {
            const show =
              variant?.featured_media?.id === mediaId ||
              state.selected_variant?.metafields?.smart?.images?.some((m) => m.id === mediaId);
            element.classList.toggle("hidden", !show);
            element.classList.toggle("order-first", variant?.featured_media?.id === mediaId);
            hydrateImages(show, element);
          });
          return;
        }
      }
      case "variant_images_and_unassigned": {
        items?.forEach(({ element, mediaId }) => {
          const show =
            variant?.featured_media?.id === mediaId ||
            !state.product.variants.map((v) => v?.featured_media?.id).includes(mediaId);
          element.classList.toggle("hidden", !show);
          element.classList.toggle("order-first", variant?.featured_media?.id === mediaId);
          hydrateImages(show, element);
        });
        return;
      }
      case "only_unassigned": {
        items?.forEach(({ element, mediaId }) => {
          const show = !state.product.variants.map((v) => v?.featured_media?.id).includes(mediaId);
          element.classList.toggle("hidden", !show);
          hydrateImages(show, element);
        });
        return;
      }
      case "first_or_selected_image": {
        items?.forEach(({ element, mediaId }) => {
          element.classList.toggle("hidden", state.selected_media_id !== mediaId);
          hydrateImages(state.selected_media_id === mediaId, element);
        });
        return;
      }
    }
  };

  const showMediaGallery = (mediaId: number) => {
    const mediaItems = mediaElements
      ?.filter(
        (item) =>
          (getComputedStyle(item.element)?.display !== "none" ||
            getComputedStyle(thumbnailMediaElements?.find((img) => img.mediaId === item.mediaId)?.element)?.display !== "none") &&
          item.media
      )
      ?.filter((a, i, arr) => arr.findIndex((b) => b.mediaId === a.mediaId) === i)
      ?.map((item) => item.media);

    const index = mediaItems.findIndex((item) => item.id === mediaId);

    _stores.mediaGallery.showGallery({
      media: mediaItems,
      index,
    });
  };

  const truncateChildren = (element: HTMLElement, container: HTMLElement, reCalculate = false) => {
    const children = [...container.children] as HTMLElement[];

    if (children.at(-2)?.offsetLeft + children.at(-2)?.clientWidth < container.clientWidth) {
      return children.length - 1;
    }

    if (container.clientWidth === 0) {
      return container.children.length;
    }

    return (
      children.findIndex(
        (childElement) =>
          childElement.offsetLeft +
            childElement.clientWidth +
            element.clientWidth +
            +getComputedStyle(container).gap.replace("px", "") +
            15 >
          container.clientWidth
      ) - 1
    );
  };

  const hasAvailableVariant = (index: number, value: string) => {
    return state?.product?.variants?.some((variant) => {
      switch (index) {
        case 0: {
          return (
            variant.options[index] === value &&
            variant.options[1] === state?.options[1] &&
            variant.options[2] === state?.options[2] &&
            variant.available
          );
        }
        case 1: {
          return (
            variant.options[0] === state?.options[0] &&
            variant.options[2] === state?.options[2] &&
            variant.options[index] === value &&
            variant.available
          );
        }
        case 2: {
          return (
            variant.options[0] === state?.options[0] &&
            variant.options[1] === state?.options[1] &&
            variant.options[index] === value &&
            variant.available
          );
        }
      }
      return false;
    });
  };

  const getContentLabels = (key: string) => {
    const labels = key.split(".").reduce<any>((acc, selector) => {
      if (!selector || acc === undefined || acc === null) return acc || "";

      if (acc && selector in acc) {
        return acc[selector];
      }
      return "";
    }, state);

    if (Array.isArray(labels)) {
      return labels.filter(Boolean);
    }
    return [labels].filter(Boolean);
  };

  const getDiscountLabel = (type: MainProductLabelsBlock["settings"]["label_type__discounts"]) => {
    const price = state.selected_variant?.price ?? 0;
    const compare_at_price = state.selected_variant?.compare_at_price ?? 0;

    if (compare_at_price <= price) {
      return "";
    }

    switch (type) {
      case "sale": {
        return "On Sale";
      }
      case "percentage": {
        return `${Math.round(((compare_at_price - price) * 100) / compare_at_price)}% off`;
      }
      case "value": {
        return `Save ${utils.formatMoney(
          compare_at_price - price,
          window?.money_format?.replace(/\{(\s*)amount(\s*)}/gi, "{$1amount_no_decimals$2}")
        )}`;
      }
      default: {
        return "";
      }
    }
  };

  const getDynamicText = (content: string) => {
    return utils.getBracketInputDynamicPluralizedText(content, state);
  };

  const getDynamicValue = (content: string) => {
    return utils.getBracketInputDynamicValue(content, state);
  };

  const getDynamicValueWithFallbacks = (content: string) => {
    return content.split(",")?.reduce((acc, item) => {
      acc ||= utils.getBracketInputDynamicValue(item.trim(), state);
      return acc;
    }, "");
  };

  const setDynamicPrice = ({
    discount = state.manual_discount,
    selected_variant = state.selected_variant,
    selling_plan = state.selected_selling_plan,
  }) => {
    let discounted_price = selected_variant?.price;
    const price_adjustment = selling_plan?.price_adjustments?.[0];

    if (price_adjustment?.value_type === "fixed_amount") {
      discounted_price = Math.max(discounted_price - Math.round(+price_adjustment?.value), 0);
    }

    if (price_adjustment?.value_type === "percentage") {
      discounted_price = Math.max(discounted_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
    }

    if (discount) {
      discounted_price = Math.max(discounted_price - Math.round((discount / 100) * discounted_price), 0);
    }

    let upsell_price_total = 0;
    let upsell_compare_at_price_total = 0;
    let upsell_triggered_quantity_limit = 9999999;

    state.upsell_items.forEach((value) => {
      let upsell_d_price = value?.variant?.price;
      if (state.selected_selling_plan) {
        const upsell_price_adjustment = value?.variant?.selling_plan_allocations?.find(
          (plan) => plan.selling_plan.id === state.selected_selling_plan.id
        )?.selling_plan?.price_adjustments?.[0];

        if (upsell_price_adjustment?.value_type === "fixed_amount") {
          upsell_d_price = Math.max(upsell_d_price - Math.round(+price_adjustment?.value), 0);
        }

        if (upsell_price_adjustment?.value_type === "percentage") {
          upsell_d_price = Math.max(upsell_d_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
        }
      }

      const upsell_available_quantity =
        value?.variant?.inventory_management === "shopify" && value?.variant?.inventory_policy === "deny"
          ? value?.variant?.inventory_quantity -
            cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
          : 999999;

      const maxMultiplier = Math.floor(upsell_available_quantity / value.quantity);

      upsell_triggered_quantity_limit = Math.min(upsell_triggered_quantity_limit, maxMultiplier);

      upsell_price_total += upsell_d_price * value.quantity;
      upsell_compare_at_price_total += Math.max(value?.variant?.compare_at_price, value?.variant?.price) * value.quantity;
    });

    state.quantity = Math.max(1, Math.min(state.quantity, state.available_quantity, upsell_triggered_quantity_limit));
    state.quantity_limit = Math.min(state.available_quantity, upsell_triggered_quantity_limit);

    state.upsell_total = upsell_price_total * state.quantity;
    state.upsell_compare_at_total = upsell_compare_at_price_total * state.quantity;

    state.selected_price = discounted_price;
    state.selected_compare_at_price = Math.max(selected_variant?.compare_at_price, selected_variant?.price);

    if (discount !== state.manual_discount) {
      state.manual_discount = discount;
    }
    if (selling_plan?.id !== state?.selected_selling_plan?.id) {
      setSellingPlan(selling_plan?.id);
    }
    if (selected_variant?.id !== state?.selected_variant?.id) {
      setSelectedVariant(selected_variant?.id);
    }
  };

  const getDataAttribute = (el) => {
    return el.hasAttribute("data-sold-out-action") && state.soldOut
      ? "sold-out-"
      : el.hasAttribute("data-preorder-action") && state.preorder
      ? "preorder-"
      : el.hasAttribute("data-subscription-action") && state.hasSubscription
      ? "subscription-"
      : el.hasAttribute("data-variant-action") && state.hasVariants
      ? "variant-"
      : "";
  };

  const getCTAButtonProps = (el: HTMLElement, preview?: CardProductCardCtaButtonBlock["settings"]["theme_editor_preview"]) => {
    let attribute = getDataAttribute(el);

    if (preview) {
      switch (preview) {
        case "default":
          attribute = "";
          break;
        case "variant":
          attribute = "variant-";
          break;
        case "subscription":
          attribute = "";
          break;
        case "out_of_stock":
          attribute = "sold-out-";
          break;
        default:
          attribute = "";
      }
    }

    return {
      content: utils.richtextWithPrices(
        el.getAttribute(`data-${attribute}content`),
        state?.selected_variant?.price,
        state.selected_variant?.compare_at_price
      ),
      action: el.getAttribute(`data-${attribute}action`),
      classes: el.getAttribute(`data-${attribute}class`),
      product_modal_handle: el.getAttribute(`data-${attribute}product-modal-handle`),
    };
  };

  const updateProductState = (product: _Product_hydrated, selectedVariantId?: number, selectedSellingPlanId?: number) => {
    if (settings?.default_variant_metafield && product) {
      const [namespace, key] = settings.default_variant_metafield.trim().split(".");
      const variant = product?.metafields?.[namespace]?.[key] as _Product_hydrated["variants"][number];
      product.selected_variant_id = variant?.id ?? product?.selected_variant_id;
    }

    const selected_variant = _product.getSelectedVariant(product, selectedVariantId);

    const selling_plan_allocations = selected_variant?.selling_plan_allocations;
    const selected_selling_plan = selectedSellingPlanId
      ? selling_plan_allocations?.find((plan) => plan.selling_plan.id === selectedSellingPlanId)?.selling_plan
      : settings?.default_select_selling_plan
      ? selling_plan_allocations?.[0]?.selling_plan
      : null;
    const selling_plan_discount_wording = +selected_selling_plan?.price_adjustments?.[0]?.value
      ? selected_selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
        ? `${utils.formatMoney(selected_selling_plan?.price_adjustments?.[0]?.value)} off`
        : selected_selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
        ? `Save ${selected_selling_plan?.price_adjustments?.[0]?.value}%`
        : ""
      : "";

    state.product_handle = product?.handle ?? state.product_handle ?? productHandle;
    state.product = product;
    if (state.selected_media_id !== product?.media?.[0]?.id) {
      state.previous_selected_media_id = state.selected_media_id;
    }
    state.selected_media_id = product?.media?.[0]?.id;
    state.properties = {};
    state.selected_variant = selected_variant;
    state.selling_plan_allocations = selling_plan_allocations;
    state.selected_selling_plan = selected_selling_plan;
    state.selling_plan_discount_wording = selling_plan_discount_wording;
    state.isAdding = false;
    state.options = selected_variant?.options;
    state.quantity = 1;
    state.sibling_handle = "";
  };

  if ((product?._recommendations_loaded_at ?? 0) < Date.now() - 1000 * 60 * 30) {
    _product.getHydratedProductData(product.handle, product.id).then((res) => {
      state.product = state.product.handle === res?.handle ? res : state.product;
      state.selected_variant =
        state.product?.variants?.find((variant) => variant.id === state.selected_variant?.id) ?? state.selected_variant;
    });
  }

  handleStickyScroll();

  Alpine.effect(() => {
    if (!document.contains($formEl)) return;
    state.isPrimary =
      !isProductBar &&
      new RegExp(`^/(products|collections/[^/]*/products)/${encodeURIComponent(productHandle)}`, "i").test(
        _stores?.router?.pathname ?? ""
      );

    if (state?.isPrimary && !window.design_mode) {
      window.onpopstate = handlePopState;

      const url = new URL(window.location.href);
      const selling_plan_id = +url.searchParams.get("selling_plan") || undefined;
      const variant_id = +url.searchParams.get("variant") || undefined;

      const remove = [];
      if (!state.selected_variant?.id) {
        remove.push("variant");
      }
      if (!state.selected_selling_plan?.id) {
        remove.push("selling_plan");
      }

      if (window?.event?.type === "popstate") {
        return;
      }

      // @ts-ignore
      if (init || window?.event?.barba_redirect) {
        if (product?.handle !== state?.product?.handle) {
          state.product = product;
          state.upsell_items = new Map();
        }
        if (variant_id !== state.selected_variant?.id) {
          state.selected_variant = _product.getSelectedVariant(product, variant_id);

          if (state.selected_media_id !== product?.media?.[0]?.id) {
            state.previous_selected_media_id = state.selected_media_id;
          }
          state.selected_media_id = product?.media?.[0]?.id;
          state.properties = {};
          state.selling_plan_allocations = selling_plan_allocations;

          let discounted_price = state.selected_variant?.price;
          const price_adjustment = state.selected_selling_plan?.price_adjustments?.[0];

          if (price_adjustment?.value_type === "fixed_amount") {
            discounted_price = Math.max(discounted_price - Math.round(+price_adjustment?.value), 0);
          }

          if (price_adjustment?.value_type === "percentage") {
            discounted_price = Math.max(discounted_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
          }

          if (state.manual_discount) {
            discounted_price = Math.max(discounted_price - Math.round((state.manual_discount / 100) * discounted_price), 0);
          }

          let upsell_price_total = 0;
          let upsell_compare_at_price_total = 0;
          let upsell_triggered_quantity_limit = 9999999;

          state.upsell_items.forEach((value) => {
            let upsell_d_price = value?.variant?.price;
            if (state.selected_selling_plan) {
              const upsell_price_adjustment = value?.variant?.selling_plan_allocations?.find(
                (plan) => plan.selling_plan.id === state.selected_selling_plan.id
              )?.selling_plan?.price_adjustments?.[0];

              if (upsell_price_adjustment?.value_type === "fixed_amount") {
                upsell_d_price = Math.max(upsell_d_price - Math.round(+price_adjustment?.value), 0);
              }

              if (upsell_price_adjustment?.value_type === "percentage") {
                upsell_d_price = Math.max(upsell_d_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
              }
            }

            const upsell_available_quantity =
              value?.variant?.inventory_management === "shopify" && value?.variant?.inventory_policy === "deny"
                ? value?.variant?.inventory_quantity -
                  cart?.state?.items?.reduce(
                    (acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc),
                    0
                  )
                : 999999;

            const maxMultiplier = Math.floor(upsell_available_quantity / value.quantity);

            upsell_triggered_quantity_limit = Math.min(upsell_triggered_quantity_limit, maxMultiplier);

            upsell_price_total += upsell_d_price * value.quantity;
            upsell_compare_at_price_total += Math.max(value?.variant?.compare_at_price, value?.variant?.price) * value.quantity;
          });

          state.quantity = Math.max(1, Math.min(state.quantity, state.available_quantity, upsell_triggered_quantity_limit));
          state.quantity_limit = Math.min(state.available_quantity, upsell_triggered_quantity_limit);

          state.upsell_total = upsell_price_total * state.quantity;
          state.upsell_compare_at_total = upsell_compare_at_price_total * state.quantity;

          state.selected_price = discounted_price;
          state.selected_compare_at_price = Math.max(state.selected_variant?.compare_at_price, state.selected_variant?.price);
          state.dynamic_buy_button = null as HTMLButtonElement;
          state.options = state.selected_variant?.options;
        }

        if (selling_plan_id !== state.selected_selling_plan?.id) {
          state.selected_selling_plan = state.selected_variant.selling_plan_allocations?.find(
            (plan) => plan.selling_plan_id === selling_plan_id
          )?.selling_plan;
        }

        return;
      }

      if (selling_plan_id !== state.selected_selling_plan?.id || variant_id !== state.selected_variant?.id) {
        url.searchParams.set("selling_plan", `${state.selected_selling_plan?.id}`);
        url.searchParams.set("variant", `${state.selected_variant?.id}`);
        remove.forEach((key) => {
          url.searchParams.delete(key);
        });

        /* @ts-ignore */
        barba.history.add(url.toString(), "barba", !variant_id ? "replace" : "replace");
      }
    }
  });

  Alpine.effect(() => {
    if (state.dynamic_buy_button) {
      state.dynamic_buy_button.disabled = !state.selected_variant?.available;

      if (!state.dynamic_buy_button?.hasAttribute("data-no-out-of-stock")) {
        state.dynamic_buy_button.classList.toggle("dynamic-buy-button-out-of-stock", !state.selected_variant?.available);
      }
    }
  });

  Alpine.effect(() => {
    if (quickView) {
      Shopify?.PaymentButton?.init();
      handleImageSelection(gallerySettings?.filter_images, mediaElements, state.selected_variant);
      if (thumbnailSettings) {
        handleImageSelection(thumbnailSettings?.thumbnail_filter_images, thumbnailMediaElements, state.selected_variant);
      }
      return;
    }
    handleImageSelection(gallerySettings?.filter_images, mediaElements, state.selected_variant);
    if (thumbnailSettings && utils.isVisible(thumbnails)) {
      const mediaItems = mediaElements?.filter((item) => getComputedStyle(item.element)?.display !== "none");
      mediaItems.forEach((item) => item.element.classList.remove("last-odd"));
      if (mediaItems.length % 2 === 1) {
        mediaItems[mediaItems.length - 1].element.classList.add("last-odd");
      }
      handleImageSelection(thumbnailSettings?.thumbnail_filter_images, thumbnailMediaElements, state.selected_variant);
    }
  });

  Alpine.effect(() => {
    let discounted_price = state.selected_variant?.price;
    const price_adjustment = state.selected_selling_plan?.price_adjustments?.[0];

    const product = state.product;
    const selected_variant = state.selected_variant;

    state.hasVariants = product?.variants?.length > 1 || product?.variants?.[0]?.title !== "Default Title";
    state.hasSubscription = !!selected_variant?.options?.length;

    state.available_quantity =
      selected_variant?.inventory_management === "shopify" && selected_variant?.inventory_policy === "deny"
        ? selected_variant?.inventory_quantity -
          cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
        : selected_variant?.preorder && selected_variant?.preorder_quantity
        ? selected_variant?.inventory_quantity +
          selected_variant?.preorder_quantity -
          cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
        : 99999;

    state.soldOut = state.available_quantity <= 0;

    state.preorder =
      selected_variant?.preorder &&
      selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0) <
        preorder_threshold;

    if (price_adjustment?.value_type === "fixed_amount") {
      discounted_price = Math.max(discounted_price - Math.round(+price_adjustment?.value), 0);
    }

    if (price_adjustment?.value_type === "percentage") {
      discounted_price = Math.max(discounted_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
    }

    if (state.manual_discount) {
      discounted_price = Math.max(discounted_price - Math.round((state.manual_discount / 100) * discounted_price), 0);
    }

    let upsell_price_total = 0;
    let upsell_compare_at_price_total = 0;
    let upsell_triggered_quantity_limit = 9999999;

    state.upsell_items.forEach((value) => {
      let upsell_d_price = value?.variant?.price;
      if (state.selected_selling_plan) {
        const upsell_price_adjustment = value?.variant?.selling_plan_allocations?.find(
          (plan) => plan.selling_plan.id === state.selected_selling_plan.id
        )?.selling_plan?.price_adjustments?.[0];

        if (upsell_price_adjustment?.value_type === "fixed_amount") {
          upsell_d_price = Math.max(upsell_d_price - Math.round(+price_adjustment?.value), 0);
        }

        if (upsell_price_adjustment?.value_type === "percentage") {
          upsell_d_price = Math.max(upsell_d_price - Math.round((+price_adjustment?.value / 100) * discounted_price), 0);
        }
      }

      const upsell_available_quantity =
        value?.variant?.inventory_management === "shopify" && value?.variant?.inventory_policy === "deny"
          ? value?.variant?.inventory_quantity -
            cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
          : value.variant?.preorder && value.variant?.preorder_quantity
          ? value.variant?.inventory_quantity +
            value.variant?.preorder_quantity -
            cart?.state?.items?.reduce((acc, line) => (line.variant_id === value.variant.id ? acc + line.quantity : acc), 0)
          : 99999;

      const maxMultiplier = Math.floor(upsell_available_quantity / value.quantity);

      upsell_triggered_quantity_limit = Math.min(upsell_triggered_quantity_limit, maxMultiplier);

      upsell_price_total += upsell_d_price * value.quantity;
      upsell_compare_at_price_total += Math.max(value?.variant?.compare_at_price, value?.variant?.price) * value.quantity;
    });

    state.quantity = Math.max(1, Math.min(state.quantity, state.available_quantity, upsell_triggered_quantity_limit));
    state.quantity_limit = Math.min(state.available_quantity, upsell_triggered_quantity_limit);

    state.upsell_total = upsell_price_total * state.quantity;
    state.upsell_compare_at_total = upsell_compare_at_price_total * state.quantity;

    state.selected_price = discounted_price;
    state.selected_compare_at_price = Math.max(state.selected_variant?.compare_at_price, state.selected_variant?.price);
  });

  const mutationObserver = new MutationObserver((e) => {
    e?.forEach((record) => {
      const nodes = [];

      record?.addedNodes?.forEach((node) => {
        if (node instanceof HTMLInputElement && node?.tagName === "INPUT" && node.name === "selling_plan" && node.value) {
          setSellingPlan(+node.value);
        }
      });
    });
  });
  mutationObserver.observe($formEl, { childList: true, subtree: true });

  if (state.isPrimary) {
    Alpine.store("main_product", {
      state,
      settings,
      handleAddToCart,
      handleBackInStockNotification,
      styleDynamicBuyButton,
      setSelectedVariant,
      setProductOption,
      setSiblingProduct,
      setSellingPlan,
      setQuantity,
      handleStickyScroll,
      scrollToMediaById,
      showMediaGallery,
      truncateChildren,
      renderProductCards,
      hasAvailableVariant,
      getContentLabels,
      getDiscountLabel,
      getDynamicText,
      getDynamicValueWithFallbacks,
    });
    const main_product = Alpine.store("main_product") as MainProductStore;
    Alpine.magic("main_product", () => main_product);
    window._stores["main_product"] = main_product;
  }
  init = false;

  return {
    pdp: state,
    settings,
    handleAddToCart,
    handleBackInStockNotification,
    styleDynamicBuyButton,
    setSelectedVariant,
    setProductOption,
    setSiblingProduct,
    setSellingPlan,
    setQuantity,
    handleStickyScroll,
    scrollToMediaById,
    showMediaGallery,
    truncateChildren,
    renderProductCards,
    hasAvailableVariant,
    getContentLabels,
    getDiscountLabel,
    getDynamicText,
    getDynamicValue,
    getDynamicValueWithFallbacks,
    getCTAButtonProps,
    updateProductState,
    setDynamicPrice,
  };
};

export type InitMainProduct = typeof initMainProduct;
export type MainProductStore = ReturnType<typeof initMainProduct>;

window._sections["initMainProduct"] = initMainProduct;
